"use client";

import { useEffect, useState, type RefObject } from "react";

/**
 * Reports true once `ref`'s element is within `rootMargin` of the viewport.
 * Unlike useLazyMount, this never gates rendering — the element stays fully
 * server-rendered and crawlable. Use it to defer *effects* (GSAP/ScrollTrigger
 * setup) that don't need to run until the section is actually about to be
 * scrolled into view, keeping their setup cost off the critical initial-load
 * main thread without hiding SEO-relevant content from the initial HTML.
 */
export function useNearViewport(ref: RefObject<Element | null>, rootMargin = "600px") {
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    if (isNear) return;
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNear(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin, isNear]);

  return isNear;
}
